import { useState, useEffect, useRef, useCallback } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { DashboardLayout } from '../../components/layout/DashboardLayout';
import { MessageCircle, Send, User, Bot, Headset, CheckCircle2, ArrowLeft, Clock } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function AdminChat() {
    const [sessions, setSessions] = useState([]);
    const [selected, setSelected] = useState(null);
    const [messages, setMessages] = useState([]);
    const [reply, setReply] = useState('');
    const [sending, setSending] = useState(false);
    const [filter, setFilter] = useState('escalated');
    const messagesEndRef = useRef(null);
    const msgContainerRef = useRef(null);
    const pollRef = useRef(null);
    const prevMsgCountRef = useRef(0);

    const fetchSessions = useCallback(async () => {
        try {
            const res = await axios.get(`${API}/api/admin/chat/sessions`, {
                params: filter !== 'all' ? { status: filter } : {},
                withCredentials: true
            });
            setSessions(res.data);
        } catch (e) {
            console.error('Failed to fetch sessions:', e);
        }
    }, [filter]);

    const fetchMessages = useCallback(async (sid) => {
        if (!sid) return;
        try {
            const res = await axios.get(`${API}/api/admin/chat/sessions/${sid}`, { withCredentials: true });
            setMessages(res.data.messages || []);
            setSelected(res.data.session);
        } catch (e) {
            console.error('Failed to fetch messages:', e);
        }
    }, []);

    useEffect(() => { fetchSessions(); }, [fetchSessions]);

    // Poll sessions list every 8s
    useEffect(() => {
        const iv = setInterval(fetchSessions, 8000);
        return () => clearInterval(iv);
    }, [fetchSessions]);

    // Poll messages when viewing a session
    useEffect(() => {
        if (!selected) { clearInterval(pollRef.current); return; }
        pollRef.current = setInterval(() => fetchMessages(selected.id), 4000);
        return () => clearInterval(pollRef.current);
    }, [selected, fetchMessages]);

    useEffect(() => {
        if (messages.length > prevMsgCountRef.current && msgContainerRef.current) {
            msgContainerRef.current.scrollTop = msgContainerRef.current.scrollHeight;
        }
        prevMsgCountRef.current = messages.length;
    }, [messages]);

    const sendReply = async () => {
        if (!reply.trim() || !selected) return;
        setSending(true);
        try {
            await axios.post(`${API}/api/admin/chat/reply/${selected.id}`, { text: reply.trim() }, { withCredentials: true });
            setReply('');
            await fetchMessages(selected.id);
        } catch (e) {
            toast.error('Failed to send reply');
        } finally {
            setSending(false);
        }
    };

    const resolve = async () => {
        if (!selected) return;
        try {
            await axios.post(`${API}/api/admin/chat/resolve/${selected.id}`, {}, { withCredentials: true });
            toast.success('Chat resolved');
            setSelected(null);
            fetchSessions();
        } catch (e) {
            toast.error('Failed to resolve');
        }
    };

    const statusBadge = (s) => {
        const map = {
            ai: 'bg-blue-500/15 text-blue-400 border-blue-500/20',
            escalated: 'bg-amber-500/15 text-amber-400 border-amber-500/20',
            resolved: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/20',
        };
        return map[s] || 'bg-white/5 text-white/40';
    };

    const roleBg = (role) => {
        if (role === 'user') return 'bg-gold/10 text-gold border-gold/20';
        if (role === 'admin') return 'bg-emerald-500/10 text-emerald-300 border-emerald-500/20';
        if (role === 'system') return 'bg-white/5 text-white/30 border-white/5 text-center italic';
        return 'bg-white/5 text-white/80 border-white/10';
    };

    return (
        <DashboardLayout type="admin">
        <div className="space-y-6" data-testid="admin-chat">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-white">Live Chat Support</h1>
                    <p className="text-sm text-muted-foreground mt-1">Manage customer conversations</p>
                </div>
                <div className="flex gap-2">
                    {['escalated', 'ai', 'resolved', 'all'].map(f => (
                        <Button
                            key={f}
                            variant={filter === f ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => { setFilter(f); setSelected(null); }}
                            className={filter === f ? 'bg-gold text-[#0A0D14]' : 'border-white/10 text-white/60'}
                        >
                            {f === 'all' ? 'All' : f.charAt(0).toUpperCase() + f.slice(1)}
                        </Button>
                    ))}
                </div>
            </div>

            <div className="grid grid-cols-3 gap-4 h-[calc(100vh-220px)]">
                {/* Sessions List */}
                <Card className="col-span-1 bg-[#0D1017] border-white/10 overflow-y-auto" data-testid="chat-sessions-list">
                    <div className="p-3 border-b border-white/10">
                        <p className="text-xs text-white/40 font-medium">{sessions.length} conversation{sessions.length !== 1 ? 's' : ''}</p>
                    </div>
                    {sessions.map(s => (
                        <button
                            key={s.id}
                            onClick={() => fetchMessages(s.id)}
                            data-testid={`chat-session-${s.id}`}
                            className={`w-full text-left p-3 border-b border-white/5 hover:bg-white/3 transition-colors ${selected?.id === s.id ? 'bg-white/5' : ''}`}
                        >
                            <div className="flex items-center justify-between mb-1">
                                <span className="text-sm font-medium text-white">{s.visitor_name}</span>
                                <span className={`text-[10px] px-1.5 py-0.5 rounded border ${statusBadge(s.status)}`}>{s.status}</span>
                            </div>
                            <p className="text-xs text-white/40 truncate">{s.last_message || 'No messages'}</p>
                            <div className="flex items-center gap-2 mt-1">
                                <Clock className="w-3 h-3 text-white/20" />
                                <span className="text-[10px] text-white/20">{new Date(s.updated_at).toLocaleString()}</span>
                                {s.unread_admin > 0 && <span className="ml-auto w-4 h-4 bg-red-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center">{s.unread_admin}</span>}
                            </div>
                        </button>
                    ))}
                    {sessions.length === 0 && (
                        <div className="p-6 text-center text-white/20 text-sm">No conversations</div>
                    )}
                </Card>

                {/* Chat View */}
                <Card className="col-span-2 bg-[#0D1017] border-white/10 flex flex-col" data-testid="chat-view">
                    {selected ? (
                        <>
                            {/* Chat Header */}
                            <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
                                <div className="flex items-center gap-3">
                                    <button onClick={() => setSelected(null)} className="text-white/40 hover:text-white lg:hidden">
                                        <ArrowLeft className="w-4 h-4" />
                                    </button>
                                    <div className="w-8 h-8 rounded-full bg-gold/10 flex items-center justify-center">
                                        <User className="w-4 h-4 text-gold" />
                                    </div>
                                    <div>
                                        <p className="text-sm font-semibold text-white">{selected.visitor_name}</p>
                                        <p className="text-[10px] text-white/40">Session: {selected.id}</p>
                                    </div>
                                    <span className={`text-[10px] px-1.5 py-0.5 rounded border ${statusBadge(selected.status)}`}>{selected.status}</span>
                                </div>
                                {selected.status !== 'resolved' && (
                                    <Button size="sm" variant="outline" onClick={resolve} className="border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10 gap-1.5" data-testid="resolve-chat-btn">
                                        <CheckCircle2 className="w-3.5 h-3.5" /> Resolve
                                    </Button>
                                )}
                            </div>

                            {/* Messages */}
                            <div ref={msgContainerRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
                                {messages.map(msg => (
                                    <div key={msg.id} className={`flex ${msg.role === 'user' ? 'justify-end' : msg.role === 'system' ? 'justify-center' : 'justify-start'}`}>
                                        <div className={`max-w-[75%] px-3 py-2 rounded-xl border text-[13px] leading-relaxed ${roleBg(msg.role)}`}>
                                            {msg.role !== 'system' && (
                                                <div className="flex items-center gap-1.5 mb-1 opacity-50">
                                                    {msg.role === 'user' ? <User className="w-3 h-3" /> : msg.role === 'admin' ? <Headset className="w-3 h-3" /> : <Bot className="w-3 h-3" />}
                                                    <span className="text-[10px]">{msg.role === 'user' ? selected.visitor_name : msg.role === 'admin' ? (msg.admin_name || 'Support') : 'AI'}</span>
                                                </div>
                                            )}
                                            <p className="whitespace-pre-wrap">{msg.text}</p>
                                        </div>
                                    </div>
                                ))}
                                <div ref={messagesEndRef} />
                            </div>

                            {/* Reply Input */}
                            {selected.status !== 'resolved' && (
                                <div className="px-4 py-3 border-t border-white/10 flex gap-2">
                                    <input
                                        data-testid="admin-chat-input"
                                        value={reply}
                                        onChange={e => setReply(e.target.value)}
                                        onKeyDown={e => e.key === 'Enter' && !e.shiftKey && sendReply()}
                                        placeholder="Type your reply..."
                                        disabled={sending}
                                        className="flex-1 px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm placeholder:text-white/25 focus:outline-none focus:border-gold/30"
                                    />
                                    <Button onClick={sendReply} disabled={!reply.trim() || sending} className="bg-gold text-[#0A0D14] hover:bg-gold/90" data-testid="admin-chat-send">
                                        <Send className="w-4 h-4" />
                                    </Button>
                                </div>
                            )}
                        </>
                    ) : (
                        <div className="flex-1 flex flex-col items-center justify-center text-white/20">
                            <MessageCircle className="w-12 h-12 mb-3 opacity-30" />
                            <p className="text-sm">Select a conversation to view</p>
                        </div>
                    )}
                </Card>
            </div>
        </div>
        </DashboardLayout>
    );
}
